import { Head, router } from "@inertiajs/react"
import { CheckCircle, Mail, MapPin, Phone, Users } from "lucide-react"

import CategoryCard from "@/components/tryouts/category-card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import VerifiedBadge from "@/components/verified-badge"
import AppLayout from "@/layouts/app-layout"
import type { TryoutsShow } from "@/types/serializers"

export default function TryoutsShowPage({
  tryout,
  categories,
  is_poster,
  has_applied,
  current_role,
}: TryoutsShow) {
  const canonicalUrl = `https://krownft.com/pruebas/${tryout.slug}`
  const pageTitle = `${tryout.title} - ${tryout.club_profile.name} | KrownFT`
  const metaDescription = `${tryout.title} organizada por ${tryout.club_profile.name} en ${[tryout.venue_name, tryout.city, tryout.country].filter(Boolean).join(", ")}.${tryout.free ? " Gratis." : ""}`
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const hasUpcomingCategories = categories.some(
    (cat) => new Date(cat.date) >= today,
  )

  return (
    <AppLayout>
      <Head title={pageTitle}>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
      </Head>

      <div className="bg-background min-h-svh">
        <div className="mx-auto max-w-4xl px-4 py-6">
          {/* Header */}
          <div className="mb-6">
            <div className="mb-2 flex items-center gap-2">
              <span className="text-krown-100 text-sm font-medium">
                {tryout.club_profile.name}
              </span>
              <VerifiedBadge show={tryout.club_profile.verified} size="sm" />
            </div>
            <h1 className="mb-3 text-3xl font-bold text-white">
              {tryout.title}
            </h1>
            <div className="flex flex-wrap items-center gap-3">
              {tryout.free && (
                <Badge className="bg-krown-accent/20 text-krown-accent border-0">
                  Gratis
                </Badge>
              )}
              <Badge variant="outline">
                {tryout.applications_count} inscriptos
              </Badge>
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            {/* Main content */}
            <div className="space-y-6 lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>Descripción</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground whitespace-pre-line">
                    {tryout.description}
                  </p>
                </CardContent>
              </Card>

              {tryout.requirements && (
                <Card>
                  <CardHeader>
                    <CardTitle>Requisitos</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground whitespace-pre-line">
                      {tryout.requirements}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Categories */}
              <div>
                <h2 className="mb-4 text-xl font-bold text-white">
                  Categorías
                </h2>
                {categories.length > 0 ? (
                  <div className="grid gap-4 sm:grid-cols-2">
                    {categories.map((cat) => (
                      <CategoryCard key={cat.id} category={cat} />
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">
                    Aún no se publicaron categorías.
                  </p>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-4">
              <Card>
                <CardContent className="space-y-3 p-4">
                  <div className="flex items-center gap-2">
                    <MapPin className="text-muted-foreground h-4 w-4" />
                    <span className="text-sm">{tryout.venue_name}</span>
                  </div>
                  {tryout.address && (
                    <div className="flex items-center gap-2">
                      <MapPin className="text-muted-foreground h-4 w-4 opacity-0" />
                      <span className="text-muted-foreground text-sm">
                        {tryout.address}
                      </span>
                    </div>
                  )}
                  {tryout.city && (
                    <div className="flex items-center gap-2">
                      <MapPin className="text-muted-foreground h-4 w-4 opacity-0" />
                      <span className="text-muted-foreground text-sm">
                        {[tryout.city, tryout.region, tryout.country]
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    </div>
                  )}
                  {tryout.contact_phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="text-muted-foreground h-4 w-4" />
                      <span className="text-sm">{tryout.contact_phone}</span>
                    </div>
                  )}
                  {tryout.contact_email && (
                    <div className="flex items-center gap-2">
                      <Mail className="text-muted-foreground h-4 w-4" />
                      <span className="text-sm">{tryout.contact_email}</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* CTA */}
              {!is_poster &&
                !has_applied &&
                hasUpcomingCategories &&
                (!current_role || current_role === "player") && (
                <Button
                  className="from-krown-orange-from to-krown-orange-to w-full bg-gradient-to-r text-white"
                  size="lg"
                  onClick={() =>
                    router.visit(`/pruebas/${tryout.slug}/inscripcion/new`)
                  }
                >
                  Inscribirme
                </Button>
              )}

              {has_applied && (
                <div className="border-krown-accent/30 bg-krown-accent/10 flex items-center gap-2 rounded-lg border p-3">
                  <CheckCircle className="text-krown-accent h-5 w-5" />
                  <span className="text-krown-accent text-sm font-medium">
                    Ya estás inscripto
                  </span>
                </div>
              )}

              {is_poster && (
                <div className="space-y-2">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() =>
                      router.visit(`/pruebas/${tryout.slug}/inscripciones`)
                    }
                  >
                    <Users className="mr-2 h-4 w-4" />
                    Ver inscripciones
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => router.visit(`/pruebas/${tryout.slug}/edit`)}
                  >
                    Editar prueba
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
